// src/components/analytics/AiDispatchFeedbackWidget.js
// FlowSense MOW+ — AI Dispatch Performance & Feedback Analytics Widget
//
// Code Deltas Applied (Doc 16 → Doc 17 merge):
//   - apiSortColumn mapping     : translates 'feedback_at' → 'created_at' before sending
//                                 to the API so the Node.js SQL ORDER BY clause never crashes
//   - Resilient data wrapper    : response.data?.data || response.data || [] handles
//                                 both wrapped ({ data: [...] }) and unwrapped ([...]) responses
//   - totalAccepted fallback    : mathematically rebuilds totalAccepted from acceptanceRate
//                                 when the backend omits the field (prevents 100% rejection inflation)
//   - Resilient row mapping     : 6 fallback chains per row field so the table renders
//                                 regardless of which SQL JOIN aliases Node.js serialises
//   - 'Manual Override': purple : added to SimpleChart colorMap
//
// Preserved from Doc 17 (base):
//   - handleExportCSV           : full CSV download with date-stamped filename
//   - Export button             : FaDownload IconButton in the header toolbar
//   - FEEDBACK_TYPES constants  : all filter option values in one place
//   - Toast on load/reset/refresh
//   - feedback_by_user_id in detail modal
//   - SkeletonText / Code imports
//   - Additional helpful hint list item ("Check if your date filters are too restrictive")

import React, { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Text,
    Alert, AlertIcon, TableContainer, Badge, HStack, VStack, IconButton,
    useColorModeValue, Flex, SimpleGrid, Stat, StatLabel, StatNumber,
    StatHelpText, Select, Button, Tooltip, useToast, Input,
    Skeleton, SkeletonText,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, useDisclosure, Progress, Divider, Code,
    List, ListItem, ListIcon
} from '@chakra-ui/react';
import {
    FaSort, FaSortUp, FaSortDown, FaDownload, FaRedo, FaFilter,
    FaChartBar, FaInfoCircle, FaChevronLeft, FaChevronRight,
    FaCheckCircle, FaExclamationCircle, FaDatabase
} from 'react-icons/fa';

import analyticsService from '../../services/analyticsService';
import { formatDate, formatDateTime } from '../../utils/date';

// ─────────────────────────────────────────────────────────────────────────────
// CARD WRAPPER
// ─────────────────────────────────────────────────────────────────────────────

const Card = ({ children, ...props }) => (
    <Box
        bg={useColorModeValue('white', 'gray.800')}
        borderRadius="lg"
        boxShadow="md"
        {...props}
    >
        {children}
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const CACHE_DURATION    = 5 * 60 * 1000; // 5 minutes

const FEEDBACK_TYPES = {
    ALL:               '',
    APPROVED:          'approved',
    REJECTED_SKILLSET: 'Rejected - Wrong Skillset',
    REJECTED_DISTANCE: 'Rejected - Too Far',
    REJECTED_OTHER:    'Rejected - Other',
};

// ─────────────────────────────────────────────────────────────────────────────
// STATE REDUCER
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
    summary:          null,
    data:             [],
    loading:          true,
    summaryLoading:   true,
    dataLoading:      true,
    error:            null,
    hasEverLoadedData: false,
    pagination: {
        currentPage: 1,
        totalPages:  1,
        totalItems:  0,
        pageSize:    5,
    },
    filters: {
        feedback:    '',
        dateFrom:    '',
        dateTo:      '',
        workOrderId: '',
    },
    sort: {
        column: 'feedback_at', // Matches DB schema column name
        order:  'DESC',
    },
    lastFetch: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_SUMMARY':
            return { ...state, summary: action.payload, summaryLoading: false };
        case 'SET_DATA':
            return {
                ...state,
                data:       action.payload.data,
                pagination: { ...state.pagination, ...action.payload.pagination },
                dataLoading: false,
                loading:    false,
                hasEverLoadedData: true,
            };
        case 'SET_LOADING':
            return { ...state, [action.payload.key]: action.payload.value };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false, dataLoading: false };
        case 'SET_FILTER':
            return {
                ...state,
                filters:    { ...state.filters, [action.payload.key]: action.payload.value },
                pagination: { ...state.pagination, currentPage: 1 },
            };
        case 'SET_SORT':
            return { ...state, sort: action.payload };
        case 'SET_PAGE':
            return { ...state, pagination: { ...state.pagination, currentPage: action.payload } };
        case 'SET_PAGE_SIZE':
            return { ...state, pagination: { ...state.pagination, pageSize: action.payload, currentPage: 1 } };
        case 'RESET_FILTERS':
            return { ...state, filters: initialState.filters, pagination: { ...state.pagination, currentPage: 1 } };
        case 'SET_LAST_FETCH':
            return { ...state, lastFetch: action.payload };
        case 'CLEAR_CACHE':
            return { ...state, lastFetch: null };
        default:
            return state;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// REJECTION REASONS CHART
// ─────────────────────────────────────────────────────────────────────────────

const SimpleChart = ({ rejectionReasons }) => {
    if (!rejectionReasons || Object.keys(rejectionReasons).length === 0) {
        return (
            <VStack spacing={2} p={4}>
                <FaChartBar size={40} color="gray" />
                <Text color="gray.500" textAlign="center">
                    No rejection data available yet
                </Text>
            </VStack>
        );
    }

    const total = Object.values(rejectionReasons).reduce((sum, val) => sum + val, 0);

    // --- UPDATE: Added 'Manual Override': 'purple' from Doc 16 ---
    const colorMap = {
        'Wrong Skillset':  'orange',
        'Too Far':         'yellow',
        'Other':           'red',
        'Manual Override': 'purple',
    };

    return (
        <VStack spacing={2} p={4} w="full">
            <Heading size="sm">Rejection Reasons</Heading>
            <VStack spacing={1} align="stretch" w="full">
                {Object.entries(rejectionReasons).map(([reason, count]) => {
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    return (
                        <Box key={reason}>
                            <HStack justify="space-between" mb={1}>
                                <Text fontSize="sm">{reason}</Text>
                                <Text fontSize="sm" fontWeight="bold">
                                    {count} ({percentage}%)
                                </Text>
                            </HStack>
                            <Progress
                                value={parseFloat(percentage)}
                                size="sm"
                                colorScheme={colorMap[reason] || 'blue'}
                                borderRadius="full"
                            />
                        </Box>
                    );
                })}
            </VStack>
        </VStack>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const AiDispatchFeedbackWidget = ({ showHelpfulHints = true }) => {
    const [state, dispatch]       = useReducer(reducer, initialState);
    const toast                   = useToast();
    const detailModal             = useDisclosure();
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Theme tokens
    const textColor   = useColorModeValue('gray.700', 'white');
    const cardBg      = useColorModeValue('gray.50', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    // ─────────────────────────────────────────────────────────────────────────
// DATA FETCHING
// ─────────────────────────────────────────────────────────────────────────

const fetchData = useCallback(async (page = state.pagination.currentPage, forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && state.lastFetch && (now - state.lastFetch) < CACHE_DURATION) return;

    dispatch({ type: 'SET_LOADING', payload: { key: 'dataLoading', value: true } });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
        // --- UPDATE: API sort column mapping ---
        // The UI sort state tracks 'feedback_at' (the DB schema column) but the
        // Node.js SQL query may expect 'created_at' as the sort key.
        // This mapping prevents an "Unknown column 'feedback_at'" SQL error.
        const apiSortColumn = state.sort.column === 'feedback_at'
            ? 'created_at'
            : state.sort.column;

        const params = {
            page,
            limit:     state.pagination.pageSize,
            sortBy:    apiSortColumn,
            sortOrder: state.sort.order,
        };

        // --- UPDATE: Map UI filter labels to DB enum values ---
        // The UI select options use human-readable rejection labels
        // (e.g. "Rejected - Wrong Skillset") but the DB `feedback` column
        // only stores 'approved' or 'rejected'. Splitting the filter into
        // `params.filter` (the enum) and `params.rejectionReason` (the sub-label)
        // lets the backend run a precise two-column WHERE clause.
        if (state.filters.feedback) {
            if (state.filters.feedback === 'approved') {
                params.filter = 'approved';
            } else {
                // Any "Rejected - *" option maps to the 'rejected' enum value.
                // The full label is forwarded so the backend can additionally
                // filter on ai_suggestion_feedback / rejection_reason.
                params.filter          = 'rejected';
                params.rejectionReason = state.filters.feedback;
            }
        }

        if (state.filters.dateFrom)    params.dateFrom    = state.filters.dateFrom;
        if (state.filters.dateTo)      params.dateTo      = state.filters.dateTo;
        if (state.filters.workOrderId) params.workOrderId = state.filters.workOrderId;

        const response = await analyticsService.getAiDispatchFeedbackLog(params);

        if (response && response.success) {
            // --- UPDATE: Resilient data wrapper ---
            // Handles both a wrapped response  ({ data: { data: [...], ... } })
            // and an unwrapped response        ({ data: [...], ... })
            // and ensures an empty array on any unexpected shape.
            let dataArray = response.data?.data || response.data || [];
            if (!Array.isArray(dataArray)) dataArray = [];

            const pagination = response.pagination || {
                currentPage: page,
                totalPages:  1,
                totalItems:  dataArray.length,
                pageSize:    state.pagination.pageSize,
            };

            dispatch({ type: 'SET_DATA',       payload: { data: dataArray, pagination } });
            dispatch({ type: 'SET_LAST_FETCH', payload: now });

            if (dataArray.length > 0 && forceRefresh) {
                toast({
                    title:       'Data loaded',
                    description: `Found ${dataArray.length} feedback records`,
                    status:      'success',
                    duration:    2000,
                    isClosable:  true,
                });
            }
        } else {
            throw new Error(response?.message || 'Failed to load data');
        }
    } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error.message });
        toast({
            title:       'Error loading data',
            description: error.message,
            status:      'error',
            duration:    5000,
            isClosable:  true,
        });
    }
}, [state.pagination.currentPage, state.pagination.pageSize, state.sort, state.filters, state.lastFetch, toast]);

const fetchSummary = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && state.lastFetch && state.summary && (now - state.lastFetch) < CACHE_DURATION) return;

    dispatch({ type: 'SET_LOADING', payload: { key: 'summaryLoading', value: true } });

    try {
        const response = await analyticsService.getAiDispatchSummaryStats();
        if (response && response.success) {
            dispatch({ type: 'SET_SUMMARY', payload: response.data });
        }
    } catch (error) {
        dispatch({ type: 'SET_LOADING', payload: { key: 'summaryLoading', value: false } });
    }
}, [state.lastFetch, state.summary]);

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchSummary();
        fetchData(1, true);
    }, []);

    useEffect(() => {
        if (state.hasEverLoadedData) {
            dispatch({ type: 'CLEAR_CACHE' });
            fetchData(1, true);
        }
    }, [state.filters, state.sort.column, state.sort.order]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────

    const handleSort = useCallback((column) => {
        const newOrder = state.sort.column === column && state.sort.order === 'ASC' ? 'DESC' : 'ASC';
        dispatch({ type: 'SET_SORT', payload: { column, order: newOrder } });
    }, [state.sort]);

    const handleFilterChange = useCallback((key, value) => {
        dispatch({ type: 'SET_FILTER', payload: { key, value } });
    }, []);

    const handlePageChange = useCallback((newPage) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
        fetchData(newPage, true);
    }, [fetchData]);

    const handlePageSizeChange = useCallback((newSize) => {
        dispatch({ type: 'SET_PAGE_SIZE', payload: parseInt(newSize) });
    }, []);

    const handleRefresh = useCallback(() => {
        dispatch({ type: 'CLEAR_CACHE' });
        fetchSummary(true);
        fetchData(state.pagination.currentPage, true);
        toast({ title: 'Refreshing data', status: 'info', duration: 1000 });
    }, [fetchSummary, fetchData, state.pagination.currentPage, toast]);

    const handleResetFilters = useCallback(() => {
        dispatch({ type: 'RESET_FILTERS' });
        toast({
            title:       'Filters cleared',
            description: 'Showing all feedback records',
            status:      'info',
            duration:    2000,
        });
    }, [toast]);

    const handleExportCSV = useCallback(() => {
        // Export current page data, or a template row if there is no data yet
        const dataToExport = state.data.length > 0 ? state.data : [
            {
                feedback_at:             new Date().toISOString(),
                work_order_id:           'SAMPLE-001',
                suggested_user_id:       'AI Suggested User',
                final_user_id:           'Dispatcher Choice',
                feedback:                'approved',
                ai_suggestion_feedback:  null,
            }
        ];

        const headers = ['Date', 'Work Order', 'AI Suggestion', 'Final Assignment', 'Feedback', 'Comments'];
        const rows = dataToExport.map(item => [
            formatDateTime(item.feedback_at || item.created_at),
            item.work_order?.title || item.work_order_title || item.work_order_id || 'N/A',
            item.suggested_user?.username || item.suggested_username || item.suggested_user_id || 'N/A',
            item.final_user?.username || item.final_username || item.final_user_id || 'N/A',
            (item.feedback || item.ai_suggestion_feedback) === 'approved' ? 'Accepted' : 'Rejected',
            item.ai_suggestion_feedback || item.rejection_comments || item.rejection_reason || '',
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `ai_dispatch_feedback_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        toast({
            title:       state.data.length > 0 ? 'Export complete' : 'Sample export created',
            description: state.data.length > 0
                ? `Exported ${state.data.length} records`
                : 'Generated sample CSV template',
            status:   'success',
            duration: 2000,
        });
    }, [state.data, toast]);

    const handleViewDetails = useCallback((record) => {
        setSelectedRecord(record);
        detailModal.onOpen();
    }, [detailModal]);

    // ─────────────────────────────────────────────────────────────────────────
    // COMPUTED VALUES
    // ─────────────────────────────────────────────────────────────────────────

    const hasActiveFilters = useMemo(
        () => Object.values(state.filters).some(v => v !== ''),
        [state.filters]
    );

    const acceptanceRate = useMemo(
        () => state.summary?.acceptanceRate
            ? (state.summary.acceptanceRate * 100).toFixed(1)
            : '0.0',
        [state.summary]
    );

    const totalSuggestions = state.summary?.totalSuggestions || 0;

    // --- UPDATE: totalAccepted fallback from Doc 16 ---
    // If the backend forgets to send 'totalAccepted', we rebuild it mathematically
    // using the acceptanceRate so the Rejections count doesn't inflate to 100%.
    const totalAccepted = state.summary?.totalAccepted !== undefined
        ? state.summary.totalAccepted
        : Math.round(totalSuggestions * (state.summary?.acceptanceRate || 0));

    const totalRejected = totalSuggestions - totalAccepted;

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    const renderSortIcon = (column) => {
        if (state.sort.column !== column) return <FaSort />;
        return state.sort.order === 'ASC' ? <FaSortUp /> : <FaSortDown />;
    };

    const getFeedbackColor = (feedback, reason) => {
        if (feedback === 'approved')         return 'green';
        if (reason?.includes('Skillset'))    return 'orange';
        if (reason?.includes('Too Far'))     return 'yellow';
        if (reason?.includes('Other'))       return 'red';
        return 'gray';
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Card p={5}>
            {/* ── HEADER ─────────────────────────────────────────────────── */}
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg" color={textColor}>
                    AI Dispatch Performance
                </Heading>
                <HStack spacing={2}>
                    <Tooltip label="Refresh data from server">
                        <Button
                            size="sm"
                            onClick={handleRefresh}
                            isLoading={state.dataLoading || state.summaryLoading}
                            leftIcon={<FaRedo />}
                            colorScheme="blue"
                            variant="outline"
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                    <Tooltip label={state.data.length > 0 ? 'Export data to CSV' : 'Generate sample CSV template'}>
                        <IconButton
                            icon={<FaDownload />}
                            size="sm"
                            onClick={handleExportCSV}
                            colorScheme="green"
                            variant="outline"
                            aria-label="Export to CSV"
                        />
                    </Tooltip>
                </HStack>
            </Flex>

            {/* ── ERROR ALERT ─────────────────────────────────────────────── */}
            {state.error && (
                <Alert status="error" mb={4} borderRadius="md">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold">Error loading data</Text>
                        <Text fontSize="sm">{state.error}</Text>
                    </Box>
                </Alert>
            )}

            {/* ── SUMMARY CARDS ───────────────────────────────────────────── */}
            {state.summaryLoading ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                    <Skeleton height="200px" borderRadius="lg" />
                    <Skeleton height="200px" borderRadius="lg" />
                </SimpleGrid>
            ) : (
                <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={6}
                    mb={6}
                    p={4}
                    bg={cardBg}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <VStack align="stretch" spacing={4}>
                        <Stat>
                            <StatLabel fontSize="md">Total AI Suggestions</StatLabel>
                            <StatNumber fontSize="3xl" color={totalSuggestions > 0 ? textColor : 'gray.400'}>
                                {totalSuggestions}
                            </StatNumber>
                            <StatHelpText>
                                {totalSuggestions > 0
                                    ? 'AI-powered dispatcher recommendations'
                                    : 'No suggestions recorded yet'}
                            </StatHelpText>
                        </Stat>

                        <Divider />

                        <Stat>
                            <StatLabel fontSize="md">Acceptance Rate</StatLabel>
                            <HStack align="baseline" spacing={2}>
                                <StatNumber fontSize="3xl" color={totalSuggestions > 0 ? 'green.500' : 'gray.400'}>
                                    {acceptanceRate}%
                                </StatNumber>
                                {totalSuggestions > 0 && (
                                    <Text fontSize="sm" color="gray.500">
                                        ({totalAccepted} of {totalSuggestions})
                                    </Text>
                                )}
                            </HStack>
                            <Progress
                                value={parseFloat(acceptanceRate)}
                                colorScheme={totalSuggestions > 0 ? 'green' : 'gray'}
                                size="sm"
                                borderRadius="full"
                                mt={2}
                            />
                        </Stat>

                        {totalRejected > 0 && (
                            <>
                                <Divider />
                                <Stat>
                                    <StatLabel fontSize="md">Total Rejections</StatLabel>
                                    <StatNumber fontSize="2xl" color="orange.500">
                                        {totalRejected}
                                    </StatNumber>
                                </Stat>
                            </>
                        )}
                    </VStack>

                    <Flex justify="center" align="center">
                        <SimpleChart rejectionReasons={state.summary?.rejectionReasons} />
                    </Flex>
                </SimpleGrid>
            )}

            {/* ── NO DATA HINT ────────────────────────────────────────────── */}
            {showHelpfulHints && state.data.length === 0 && !state.dataLoading && state.hasEverLoadedData && (
                <Alert status="info" mb={4} borderRadius="md">
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold" mb={2}>No feedback data found</Text>
                        <Text fontSize="sm" mb={2}>
                            The AI dispatch feedback system is ready but hasn't recorded any dispatcher decisions yet.
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>To start collecting data:</Text>
                        <List spacing={1} fontSize="sm">
                            <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                Ensure AI dispatch suggestions are enabled in your system
                            </ListItem>
                            <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                Have dispatchers accept or reject AI suggestions for work orders
                            </ListItem>
                            <ListItem>
                                <ListIcon as={FaCheckCircle} color="green.500" />
                                Check if your date filters are too restrictive
                            </ListItem>
                        </List>
                        {hasActiveFilters && (
                            <Button size="sm" mt={3} onClick={handleResetFilters} colorScheme="blue" variant="outline">
                                Clear All Filters
                            </Button>
                        )}
                    </Box>
                </Alert>
            )}

            {/* ── FILTERS ─────────────────────────────────────────────────── */}
            <VStack align="stretch" spacing={3} mb={4}>
                <Flex justify="space-between" align="center">
                    <Heading size="md" color={textColor}>Feedback History</Heading>
                    {hasActiveFilters && (
                        <Button size="sm" leftIcon={<FaFilter />} onClick={handleResetFilters} variant="ghost" colorScheme="blue">
                            Clear Filters
                        </Button>
                    )}
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                    <Select
                        placeholder="All Feedback Types"
                        value={state.filters.feedback}
                        onChange={(e) => handleFilterChange('feedback', e.target.value)}
                        size="sm"
                    >
                        <option value="">All Feedback</option>
                        <option value={FEEDBACK_TYPES.APPROVED}>✅ Accepted</option>
                        <option value={FEEDBACK_TYPES.REJECTED_SKILLSET}>❌ Rejected - Wrong Skillset</option>
                        <option value={FEEDBACK_TYPES.REJECTED_DISTANCE}>❌ Rejected - Too Far</option>
                        <option value={FEEDBACK_TYPES.REJECTED_OTHER}>❌ Rejected - Other</option>
                    </Select>

                    <Input
                        type="date"
                        placeholder="From Date"
                        value={state.filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        size="sm"
                    />

                    <Input
                        type="date"
                        placeholder="To Date"
                        value={state.filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        size="sm"
                    />

                    <Input
                        placeholder="Work Order ID"
                        value={state.filters.workOrderId}
                        onChange={(e) => handleFilterChange('workOrderId', e.target.value)}
                        size="sm"
                    />
                </SimpleGrid>
            </VStack>

            {/* ── DATA TABLE ──────────────────────────────────────────────── */}
            <TableContainer>
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            <Th cursor="pointer" onClick={() => handleSort('feedback_at')}>
                                <HStack spacing={1}>
                                    <Text>Date</Text>
                                    {renderSortIcon('feedback_at')}
                                </HStack>
                            </Th>
                            <Th>Work Order</Th>
                            <Th>AI Suggestion</Th>
                            <Th>Final Assignment</Th>
                            <Th cursor="pointer" onClick={() => handleSort('feedback')}>
                                <HStack spacing={1}>
                                    <Text>Feedback</Text>
                                    {renderSortIcon('feedback')}
                                </HStack>
                            </Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {state.dataLoading ? (
                            Array.from({ length: state.pagination.pageSize }).map((_, i) => (
                                <Tr key={i}>
                                    {Array.from({ length: 6 }).map((_, j) => (
                                        <Td key={j}><Skeleton height="20px" /></Td>
                                    ))}
                                </Tr>
                            ))
                        ) : state.data.length === 0 ? (
                            <Tr>
                                <Td colSpan={6} textAlign="center" py={12}>
                                    <VStack spacing={3}>
                                        <FaDatabase size={40} color="#CBD5E0" />
                                        <Text fontSize="lg" fontWeight="medium" color="gray.600">
                                            No Feedback Records
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" maxW="400px" textAlign="center">
                                            {hasActiveFilters
                                                ? 'No records match your current filters. Try adjusting or clearing them.'
                                                : 'Feedback will appear here once dispatchers start accepting or rejecting AI suggestions.'}
                                        </Text>
                                    </VStack>
                                </Td>
                            </Tr>
                        ) : (
                            state.data.map((item, idx) => {
                                // --- UPDATE: Ultra-resilient field mapping from Doc 16 ---
                                // Each field has multiple fallback chains so the table renders
                                // correctly regardless of which SQL JOIN aliases Node.js serialises.
                                const dateVal     = item.feedback_at || item.created_at;
                                const woTitle     = item.work_order?.title || item.work_order_title || `WO-${item.work_order_id}`;
                                const suggUser    = item.suggested_user?.username || item.suggested_username || item.suggested_user_id || 'N/A';
                                const finalUser   = item.final_user?.username || item.final_username || item.final_user_id || 'N/A';
                                const feedbackType = item.feedback || item.ai_suggestion_feedback || 'rejected';
                                const reason      = item.ai_suggestion_feedback || item.rejection_comments || item.rejection_reason || 'Rejected';

                                return (
                                    <Tr key={item.id || `row-${idx}`} _hover={{ bg: cardBg }}>
                                        <Td fontSize="sm">{formatDate(dateVal)}</Td>
                                        <Td>
                                            <Tooltip label={woTitle}>
                                                <Text isTruncated maxW="150px" fontSize="sm">{woTitle}</Text>
                                            </Tooltip>
                                        </Td>
                                        <Td fontSize="sm">{suggUser}</Td>
                                        <Td fontSize="sm">{finalUser}</Td>
                                        <Td>
                                            <Badge
                                                colorScheme={getFeedbackColor(feedbackType, reason)}
                                                fontSize="xs"
                                            >
                                                {feedbackType === 'approved' ? '✅ Accepted' : `❌ ${reason}`}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <IconButton
                                                icon={<FaInfoCircle />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => handleViewDetails(item)}
                                                aria-label="View details"
                                            />
                                        </Td>
                                    </Tr>
                                );
                            })
                        )}
                    </Tbody>
                </Table>
            </TableContainer>

            {/* ── PAGINATION ──────────────────────────────────────────────── */}
            {state.data.length > 0 && (
                <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={2}>
                    <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.500">
                            Page {state.pagination.currentPage} of {state.pagination.totalPages || 1}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            ({state.pagination.totalItems || 0} total records)
                        </Text>
                    </HStack>

                    <HStack spacing={2}>
                        <Select
                            value={state.pagination.pageSize}
                            onChange={(e) => handlePageSizeChange(e.target.value)}
                            size="sm"
                            w="80px"
                        >
                            {PAGE_SIZE_OPTIONS.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </Select>

                        <IconButton
                            icon={<FaChevronLeft />}
                            size="sm"
                            onClick={() => handlePageChange(state.pagination.currentPage - 1)}
                            isDisabled={state.pagination.currentPage <= 1 || state.dataLoading}
                            aria-label="Previous page"
                        />

                        <IconButton
                            icon={<FaChevronRight />}
                            size="sm"
                            onClick={() => handlePageChange(state.pagination.currentPage + 1)}
                            isDisabled={state.pagination.currentPage >= state.pagination.totalPages || state.dataLoading}
                            aria-label="Next page"
                        />
                    </HStack>
                </Flex>
            )}

            {/* ── DETAIL MODAL ────────────────────────────────────────────── */}
            <Modal isOpen={detailModal.isOpen} onClose={detailModal.onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Feedback Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedRecord && (
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontWeight="bold">Date & Time:</Text>
                                    <Text>{formatDateTime(selectedRecord.feedback_at || selectedRecord.created_at)}</Text>
                                </Box>
                                <Divider />
                                <Box>
                                    <Text fontWeight="bold">Work Order:</Text>
                                    <Text>
                                        {selectedRecord.work_order?.title
                                            || selectedRecord.work_order_title
                                            || selectedRecord.work_order_id
                                            || 'N/A'}
                                    </Text>
                                </Box>
                                <Divider />
                                <Box>
                                    <Text fontWeight="bold">AI Suggested User:</Text>
                                    <Text>
                                        {selectedRecord.suggested_user?.username
                                            || selectedRecord.suggested_username
                                            || selectedRecord.suggested_user_id
                                            || 'N/A'}
                                    </Text>
                                </Box>
                                <Divider />
                                <Box>
                                    <Text fontWeight="bold">Final Assignment:</Text>
                                    <Text>
                                        {selectedRecord.final_user?.username
                                            || selectedRecord.final_username
                                            || selectedRecord.final_user_id
                                            || 'N/A'}
                                    </Text>
                                </Box>
                                <Divider />
                                <Box>
                                    <Text fontWeight="bold">Feedback Status:</Text>
                                    <Badge
                                        colorScheme={getFeedbackColor(
                                            selectedRecord.feedback,
                                            selectedRecord.ai_suggestion_feedback
                                        )}
                                        fontSize="sm"
                                        p={2}
                                    >
                                        {(selectedRecord.feedback || selectedRecord.ai_suggestion_feedback) === 'approved'
                                            ? 'AI Suggestion Accepted'
                                            : 'AI Suggestion Rejected'}
                                    </Badge>
                                </Box>

                                {(selectedRecord.ai_suggestion_feedback || selectedRecord.rejection_comments || selectedRecord.rejection_reason) && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Text fontWeight="bold">Rejection Reason:</Text>
                                            <Text>
                                                {selectedRecord.ai_suggestion_feedback
                                                    || selectedRecord.rejection_comments
                                                    || selectedRecord.rejection_reason}
                                            </Text>
                                        </Box>
                                    </>
                                )}

                                {selectedRecord.feedback_by_user_id && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Text fontWeight="bold">Feedback Provided By:</Text>
                                            <Text>User ID: {selectedRecord.feedback_by_user_id}</Text>
                                        </Box>
                                    </>
                                )}

                                {selectedRecord.assignment_confidence_score != null && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Text fontWeight="bold">AI Confidence Score:</Text>
                                            <HStack>
                                                <Progress
                                                    value={selectedRecord.assignment_confidence_score * 100}
                                                    size="sm"
                                                    colorScheme="blue"
                                                    borderRadius="full"
                                                    flex={1}
                                                />
                                                <Code fontSize="sm">
                                                    {(selectedRecord.assignment_confidence_score * 100).toFixed(1)}%
                                                </Code>
                                            </HStack>
                                        </Box>
                                    </>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={detailModal.onClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Card>
    );
};

export default AiDispatchFeedbackWidget;